/** @file ArtScreen: the Art screen body: parts, slot cards per direction, turntable, lint, ask agent. */
import { parseSlotKey } from '@zilo/pose3d-format/derive/parseSlotKey';
import type { DragEvent, ReactElement } from 'react';
import { useMemo, useRef, useState } from 'react';
import { useEditor } from '../../state/useEditor';
import { useEditorStore } from '../../state/useEditorStore';
import { artActions } from './artActions';
import { artClient } from './artClient';
import { artKeyHandlers } from './artKeyHandlers';
import { BulkDialogs } from './BulkDialogs';
import type { ArtDeps, BriefMode } from './artTypes';
import { ArtToolbar } from './ArtToolbar';
import { AskAgentField } from './AskAgentField';
import { LintList } from './LintList';
import { PartsList } from './PartsList';
import { SheetDialogs } from './sheet/SheetDialogs';
import { useSheetImport } from './sheet/useSheetImport';
import { SlotGrid } from './SlotGrid';
import { slotMenu } from './slotMenu';
import { slotPrompt } from './slotPrompt';
import { useArtKeys } from './useArtKeys';
import { useArtModel } from './useArtModel';
import { useBulkUpload } from './useBulkUpload';
import { useSlotMaskUrl } from './useSlotMaskUrl';
import { useStyleNotes } from './useStyleNotes';

/** `deps` replaces the api client built from `session.url` (dev pages and tests have no server). */
export type ArtScreenProps = { deps?: ArtDeps };

/**
 * Three columns under a toolbar: the parts list, the selected part's slot cards, and a right column with the
 * turntable placeholder (`data-panel="turntable"`, filled by the shell), the slot lint list and the ask-agent field.
 */
export function ArtScreen({ deps }: ArtScreenProps): ReactElement {
  const store = useEditorStore();
  const url = useEditor((s) => s.session.url);
  const bundle = useEditor((s) => s.preview.bundle);
  const server = useMemo(() => deps ?? artClient(url), [deps, url]);
  const actions = useMemo(() => artActions(store, server), [store, server]);
  const m = useArtModel(server);
  const pickerRef = useRef<HTMLInputElement>(null);
  const maskUrl = useSlotMaskUrl(server, m.puppetId, m.selected?.key ?? null, m.selected?.status === 'empty');
  const select = (key: string): void => { store.getState().select({ slot: key, drawable: parseSlotKey(key)?.drawable ?? null }); };
  const clear = (key: string): void => { if (window.confirm(`Clear slot ${key}?`)) void actions.clear(key); };
  const upload = (): void => { pickerRef.current?.click(); };
  const notifyError = (text: string): void => { store.getState().notify('error', text); };
  const [styleNotes, setStyleNotes] = useStyleNotes(m.puppetId);
  const [includeFilled, setIncludeFilled] = useState(false);
  const [briefMode, setBriefMode] = useState<BriefMode>('slots');
  const bulk = useBulkUpload(actions, m.all, notifyError);
  const revision = useEditor((s) => s.puppet.doc?.revision ?? 0);
  const sheets = useSheetImport({ actions, deps: server, puppet: m.puppet, puppetId: m.puppetId, revision, notify: (kind, text) => { store.getState().notify(kind, text); } });
  const briefInput = { views: m.views, style: styleNotes, includeFilled, mode: briefMode };
  const takeFiles = (e: DragEvent): void => {
    e.preventDefault();
    const files = [...e.dataTransfer.files];
    const one = files.length === 1 ? files[0] : undefined;
    void (one === undefined ? Promise.resolve(false) : sheets.open(one)).then((taken) => { if (!taken) void bulk.start(files); });
  };
  useArtKeys(artKeyHandlers({
    slots: m.slots, selected: m.selected?.key ?? null, select, clear, upload,
    mask: (key) => { void actions.downloadMask(key, 'silhouette'); },
  }));
  const openMenu = (key: string, x: number, y: number): void => {
    const slot = m.slots.find((s) => s.key === key);
    if (slot === undefined) return;
    const twin = m.all.find((s) => s.key === slot.mirroredFrom);
    store.getState().openMenu(x, y, slotMenu(slot, {
      upload, clear: () => { clear(key); },
      paint: () => { store.getState().openTile({ drawable: slot.drawable, entry: slot.entry, tile: slot.tile }); },
      mask: (style) => { void actions.downloadMask(key, style); },
      copyMirrored: () => { if (slot.mirroredFrom !== undefined) void actions.copyFrom(key, slot.mirroredFrom); },
      ask: () => { void actions.askAgent(slotPrompt(slot)); },
    }, twin?.status === 'filled'));
  };
  if (m.puppet === null) return <main style={{ padding: 24, color: '#a9adb6' }}>No puppet open.</main>;
  return (
    <main data-screen="art" onDragOver={(e) => { e.preventDefault(); }} onDrop={takeFiles} style={{ display: 'grid', gridTemplateRows: 'auto 1fr', height: '100%', minHeight: 0, color: '#e6e8ec', background: '#1d1f24', fontFamily: 'system-ui, sans-serif' }}>
      <ArtToolbar
        profile={m.puppet.profile}
        filled={m.filled}
        total={m.all.length}
        style={styleNotes}
        onStyle={setStyleNotes}
        includeFilled={includeFilled}
        onIncludeFilled={setIncludeFilled}
        mode={briefMode}
        onMode={setBriefMode}
        onGuide={sheets.openGuide}
        onSheet={(file, kind) => { void sheets.open(file, kind); }}
        onBrief={() => { void actions.copyBrief(briefInput); }}
        onBriefDownload={() => { actions.downloadBrief(briefInput); }}
        onFiles={(files) => { void bulk.start(files); }}
        onExport={() => { actions.exportSlots(m.views); }}
        onPack={() => { void actions.packSheet(); }}
      />
      <div style={{ display: 'grid', gridTemplateColumns: '240px minmax(0, 1fr) 300px', minHeight: 0 }}>
        <PartsList parts={m.parts} selected={m.drawable} onSelect={(d) => { store.getState().select({ drawable: d, slot: null }); }} />
        <SlotGrid
          slots={m.slots}
          states={m.states}
          selected={m.selected?.key ?? null}
          bundle={bundle}
          maskUrl={maskUrl}
          pickerRef={pickerRef}
          onSelect={select}
          onMenu={openMenu}
          onFill={(key, png, fit) => { void actions.fill(key, png, fit); }}
          onError={notifyError}
        />
        <aside style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 8, minHeight: 0, borderLeft: '1px solid #33363d' }}>
          <div data-panel="turntable" style={{ flex: 'none', height: 240, background: '#15171b', borderRadius: 6 }} />
          <LintList items={m.warnings} onJump={select} />
          <AskAgentField suggestion={m.selected === null ? '' : slotPrompt(m.selected)} onAsk={(text) => { void actions.askAgent(text); }} />
        </aside>
      </div>
      <BulkDialogs bulk={bulk} />
      <SheetDialogs sheets={sheets} puppet={m.puppet} onCopyGuideBrief={() => { void actions.copyBrief({ ...briefInput, mode: 'guide' }); }} />
    </main>
  );
}
